import { useRef, useState } from 'react'
import { palettePrimaryMain } from '@/theme/designTokens'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useRequestPasswordReset } from '../api'
import {
  forgotPasswordSchema,
  type ForgotPasswordFormData,
} from '../lib'
import { ApiError } from '@/api/http/types'
import { BackToLoginButton } from '../components'
import lockPasswordIcon from '@/assets/lock-password-icon.svg'
import checkboxCircleIcon from '@/assets/checkbox-circle-icon.svg'

const inputSx = {
  height: 50,
  bgcolor: '#ffffff',
  borderRadius: '8px',
  fontSize: '14px',
  letterSpacing: '-0.9525px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ededed',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ededed',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ededed',
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-input': {
    px: '16px',
    py: '14px',
    color: '#04183f',
    '&::placeholder': {
      color: '#9ca3af',
      opacity: 1,
    },
  },
} as const

const errorInputSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ef4444',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ef4444',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ef4444',
    borderWidth: '1px',
  },
} as const

const headingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '32px',
  lineHeight: '40px',
  letterSpacing: '0.4063px',
  color: '#23262f',
  textAlign: 'center',
} as const

const subtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#061e32',
  textAlign: 'center',
} as const

interface IconCircleProps {
  readonly bgcolor: string
  readonly icon: string
}

function IconCircle({ bgcolor, icon }: IconCircleProps) {
  return (
    <Box
      sx={{
        width: 64,
        height: 64,
        bgcolor,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Box component="img" src={icon} alt="" sx={{ width: 31, height: 31 }} />
    </Box>
  )
}

function getServerEmailError(error: unknown): string {
  if (!(error instanceof ApiError))
    return 'An unexpected error occurred. Please try again.'
  if (error.isNotFound) return 'Invalid: Email not found'
  if (error.isRateLimited)
    return 'Please wait 5 minutes before requesting again.'
  return error.message
}

type Step = 'form' | 'success'

export function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('form')
  const submittedEmailRef = useRef('')
  const [resendError, setResendError] = useState<string | null>(null)

  const resetRequest = useRequestPasswordReset()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  })

  useDocumentTitle(
    step === 'form' ? 'Forgot Password' : 'A Password Reset Link Has Been Sent',
  )

  const onSubmit = (data: ForgotPasswordFormData) => {
    resetRequest.mutate(
      { email: data.email },
      {
        onSuccess: () => {
          submittedEmailRef.current = data.email
          setStep('success')
        },
        onError: (error) => {
          setError('email', {
            type: 'server',
            message: getServerEmailError(error),
          })
        },
      },
    )
  }

  const handleResend = () => {
    setResendError(null)
    resetRequest.mutate(
      { email: submittedEmailRef.current },
      {
        onError: (error) => {
          setResendError(getServerEmailError(error))
        },
      },
    )
  }

  const emailReg = register('email')
  const emailError = errors.email?.message

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f9fafb',
      }}
    >
      <Box
        sx={{
          width: 464,
          bgcolor: '#ffffff',
          borderRadius: '16px',
          boxShadow:
            '0px 10px 15px 0px rgba(0,0,0,0.1), 0px 4px 6px 0px rgba(0,0,0,0.1)',
          pt: step === 'form' ? '43px' : '30px',
          pb: step === 'form' ? '48px' : '30px',
          px: '32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {step === 'form' ? (
          <>
            <IconCircle bgcolor="#e0f1fe" icon={lockPasswordIcon} />

            <Typography component="h1" sx={{ ...headingSx, mt: '16px' }}>
              Forgot Password
            </Typography>

            <Typography sx={{ ...subtitleSx, mt: '17px' }}>
              Enter your email to receive a password reset link.
            </Typography>

            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
              sx={{
                mt: '27px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '-0.9525px',
                    color: '#0f172a',
                    mb: '8px',
                  }}
                >
                  Email
                </Typography>
                <OutlinedInput
                  placeholder="Enter your email"
                  fullWidth
                  autoComplete="email"
                  error={!!emailError}
                  inputRef={emailReg.ref}
                  name={emailReg.name}
                  onChange={emailReg.onChange}
                  onBlur={emailReg.onBlur}
                  sx={{ ...inputSx, ...(emailError ? errorInputSx : {}) }}
                />
              </Box>

              {emailError && (
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '13px',
                    lineHeight: '20px',
                    letterSpacing: '-0.1504px',
                    color: '#ef4444',
                    mt: '-12px',
                  }}
                >
                  {emailError}
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disableElevation
                disabled={resetRequest.isPending}
                sx={{
                  bgcolor: palettePrimaryMain,
                  borderRadius: '12px',
                  py: '16px',
                  px: '24px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px',
                  letterSpacing: 0,
                  color: '#ffffff',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#007bb8',
                  },
                }}
              >
                {resetRequest.isPending ? (
                  <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                ) : (
                  'Send Email'
                )}
              </Button>

              <BackToLoginButton />
            </Box>
          </>
        ) : (
          <>
            <IconCircle bgcolor="#f0fdf4" icon={checkboxCircleIcon} />

            <Typography component="h1" sx={{ ...headingSx, mt: '22px' }}>
              A Password Reset Link Has Been Sent
            </Typography>

            <Typography sx={{ ...subtitleSx, mt: '8px' }}>
              A password reset link has been sent to{' '}
              {submittedEmailRef.current}.
            </Typography>

            <Box
              sx={{
                mt: '26px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              }}
            >
              {/* Business logic addition — not in Figma */}
              {resendError && (
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '13px',
                    lineHeight: '20px',
                    letterSpacing: '-0.1504px',
                    color: '#ef4444',
                    mt: '-12px',
                  }}
                >
                  {resendError}
                </Typography>
              )}

              <Button
                type="button"
                variant="contained"
                fullWidth
                disableElevation
                disabled={resetRequest.isPending}
                onClick={handleResend}
                sx={{
                  bgcolor: palettePrimaryMain,
                  borderRadius: '8px',
                  height: 52,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '-0.3125px',
                  color: '#ffffff',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#007bb8',
                  },
                }}
              >
                {resetRequest.isPending ? (
                  <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                ) : (
                  'Send Email'
                )}
              </Button>

              <BackToLoginButton />
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
